import { Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
	DefaultValues,
	FormProvider,
	SubmitHandler,
	useForm,
} from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { FormType } from '@/api/enum/formType.enum';
import {
	Dispatch,
	forwardRef,
	SetStateAction,
	useEffect,
	useImperativeHandle,
} from 'react';
import AppDatePicker from '@/common/components/form-inputs/AppDatePicker';
import dayjs from 'dayjs';
import { API_DATE_FORMAT, notFutureDate } from '@/utils/dateHelper';
import AppTextField from '@/common/components/form-inputs/AppTextField';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import {
	ACCEPT,
	CONSENT,
	EMERGENCY_CONTACT,
	SEX_LIST,
} from '@/common/constants/common';
import AppDropDownMenu from '@/common/components/form-inputs/AppDropDownMenu';
import AppMultipleDropDownMenu from '@/common/components/form-inputs/AppMultipleDropDownMenu';
import { useGetDicListQuery, useGetStaffAllListQuery } from '@/api/common';
import { useSelector } from 'react-redux';
import { selectMyInfo } from '@/pages/auth/authSlice';

interface Props {
	type: FormType;
	details?: any;
	setType: Dispatch<SetStateAction<FormType>>;
	allEnumList: any;
}

const EditAcceptApplicationForm = forwardRef(
	({ type, setType, details, allEnumList }: Props, ref) => {
		const { t } = useTranslation();
		const userInfo = useSelector(selectMyInfo);
		const defaultValues: DefaultValues<any> = {
			acceptApplication: null,
			approachSource: null,
		};

		const { data: staffList }: any = useGetStaffAllListQuery(
			userInfo?.currentBranch?.id || '',
			{
				refetchOnMountOrArgChange: true,
				skip: !userInfo?.currentBranch,
			}
		);

		const { data: mentalHealthConditionDicList } = useGetDicListQuery(
			'mental_health_condition',
			{ refetchOnMountOrArgChange: true }
		);

		useEffect(() => {
			if (details) methods.reset(details);
		}, [details]);

		const formSchema = Yup.object().shape({
			acceptApplication: Yup.string().required(t('errorMessage.pleaseSelect')),
			approachSource: Yup.string().required(t('errorMessage.pleaseSelect')),
			serviceRecommendedSpecify: Yup.string().when('serviceRecommendedList', {
				is: (serviceRecommendedList: string) =>
					serviceRecommendedList && serviceRecommendedListIsOther(),
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			referredOrganisation: Yup.string().when('approachSource', {
				is: '2',
				then: (schema) => schema.required(t('errorMessage.pleaseEnter')),
				otherwise: (schema) => schema.nullable(),
			}),
			meetingDate: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
			onDutyStaffSignatureDate: Yup.string()
				.nullable()
				.test(notFutureDate(t('errorMessage.dateCannotBeFuture'))),
		});
		const methods = useForm<any>({
			defaultValues,
			resolver: yupResolver(formSchema),
			mode: 'onChange',
			shouldFocusError: true,
		});

		const watchServiceRecommendedList = methods.watch('serviceRecommendedList');
		const watchApproachSource = methods.watch('approachSource');

		useImperativeHandle(ref, () => ({
			getFormData: () => methods.getValues(),
			validateForm: () => {
				methods.trigger();
				methods.formState.isValid;
				return methods.trigger().then(() => methods.formState.isValid);
			},
		}));

		const serviceRecommendedListIsOther = () => {
			const obj = allEnumList?.data.ServiceRecommendedEnum.find(
				(item: any) => item.desc == '其他'
			);
			const isDisable = (watchServiceRecommendedList || []).find(
				(item: any) => item == obj.code
			);
			if (isDisable) return true;
			return false;
		};

		return (
			<>
				<FormProvider {...methods}>
					<Typography variant='h6' sx={{ fontWeight: 'bold' }} marginBottom={2}>
						{t('preAdmission.acceptApplicationForm')}
					</Typography>
					<form onSubmit={methods.handleSubmit((e: any) => e.preventDefault())}>
						<Grid container spacing={3} marginTop={0} marginBottom={3}>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.acceptApplication') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='acceptApplication'
									row
									options={ACCEPT}
								/>
							</Grid>
							<Grid size={12}>
								<Typography variant='body2' color='common.neutral40'>
									{t('preAdmission.zh_approachSource') + '*'}
								</Typography>
								<AppRadioGroup
									control={methods.control}
									name='approachSource'
									row
									options={(allEnumList?.data.ApproachSourceEnum || []).map(
										(item: any) => {
											return {
												...item,
												id: item.code,
												name: item.descZh,
											};
										}
									)}
									handleChange={() => {
										methods.setValue('referredOrganisation', '');
										methods.trigger('referredOrganisation');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={watchApproachSource != 2}
									control={methods.control}
									name='referredOrganisation'
									label={`${t('preAdmission.referredOrganisation')}${
										watchApproachSource == 2 ? '*' : ''
									}`}
								/>
							</Grid>
							<Grid size={6}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='mentalHealthConditionList'
									label={t('preAdmission.mentalHealthConditionList')}
									// Use labelEn here: the API does not provide a labelZh for this field.
									options={(mentalHealthConditionDicList?.data || []).map(
										(item: { labelEn: any }) => {
											return {
												...item,
												name: item.labelEn,
											};
										}
									)}
								/>
							</Grid>
							<Grid size={6}>
								<AppMultipleDropDownMenu
									control={methods.control}
									name='serviceRecommendedList'
									label={t('preAdmission.serviceRecommendedList')}
									options={(allEnumList?.data.ServiceRecommendedEnum || []).map(
										(item: any) => {
											return {
												...item,
												id: item.code,
												name: item.desc,
											};
										}
									)}
									handleChange={() => {
										methods.setValue('serviceRecommendedSpecify', '');
										methods.clearErrors('serviceRecommendedSpecify');
									}}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									disabled={!serviceRecommendedListIsOther()}
									control={methods.control}
									name='serviceRecommendedSpecify'
									label={`${t('preAdmission.serviceRecommendedListDetail')}${
										serviceRecommendedListIsOther() ? '*' : ''
									}`}
								/>
							</Grid>
							<Grid size={6}>
								<AppTextField
									control={methods.control}
									name='membershipApplicationRemarks'
									label={t('preAdmission.membershipApplicationRemarks')}
								/>
							</Grid>
							<Grid size={6}>
								<AppDropDownMenu
									control={methods.control}
									name='interventionWorker'
									label={t('preAdmission.interventionWorker')}
									options={staffList?.data || []}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='meetingDate'
									label={t('preAdmission.meetingDate')}
								/>
							</Grid>
							<Grid size={6}>
								<AppDatePicker
									maximumDate={dayjs().startOf('day').format(API_DATE_FORMAT)}
									control={methods.control}
									name='onDutyStaffSignatureDate'
									label={t('preAdmission.onDutyStaffSignatureDate')}
								/>
							</Grid>
						</Grid>
					</form>
				</FormProvider>
			</>
		);
	}
);

EditAcceptApplicationForm.displayName = 'EditAcceptApplicationForm';

export default EditAcceptApplicationForm;
